/**
 * Cart Print Summary
 * Handles print functionality for individual bundles and full cart summaries.
 * Opens print-friendly pages in new windows for saving/printing.
 */

class CartPrintSummary {
  constructor() {
    this.init();
  }

  /**
   * Initializes print button event handlers
   */
  init() {
    document.addEventListener('click', (event) => {
      const printButton = event.target.closest('[data-bundle-print]');
      if (printButton) {
        event.preventDefault();
        const groupId = printButton.dataset.bundlePrint;
        this.handlePrint(groupId);
      }

      const cartPrintButton = event.target.closest('[data-cart-print-summary]');
      if (cartPrintButton) {
        event.preventDefault();
        this.handleCartPrint();
      }
    });
  }

  /**
   * Filters out internal/system properties from bespoke options
   * Keeps user-facing values including confirmation checkbox values ("Yes")
   * @param {Object} properties - Item properties object
   * @returns {Object} Filtered properties
   */
  filterBespokeProperties(properties) {
    const systemKeys = ['GroupId', 'groupedProduct', 'required', 'min', 'aog', 'tlp', 'hasOptions', 'next_day'];
    return Object.fromEntries(
      Object.entries(properties).filter(([key]) => {
        const lowerKey = key.toLowerCase();
        return !systemKeys.includes(key) &&
          !key.startsWith('_') &&
          !lowerKey.startsWith('bespoke');
      })
    );
  }

  /**
   * Formats price in pence to currency string
   * @param {number} pence - Price in pence
   * @param {string} currency - Currency code
   * @returns {string} Formatted price
   */
  formatPrice(pence, currency = 'GBP') {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency
    }).format(pence / 100);
  }

  /**
   * Handles bundle print action
   * Opens a new window with print-friendly bundle details
   * @param {string} groupId - Bundle GroupId
   */
  async handlePrint(groupId) {
    const bundleLine = document.querySelector(`[data-bundle-group="${groupId}"]`);
    if (!bundleLine) return;

    const bundleData = await this.collectPrintData(groupId);
    if (!bundleData) return;

    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) return;

    const html = this.buildPrintHtml(bundleData);
    printWindow.document.write(html);
    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.print();
    };
  }

  /**
   * Collects bundle data for print from cart API
   * @param {string} groupId - Bundle GroupId
   * @returns {Promise<Object|null>} Print data object
   */
  async collectPrintData(groupId) {
    try {
      const cartResponse = await fetch('/cart.js', {
        headers: { 'Accept': 'application/json' }
      });
      const cart = await cartResponse.json();

      const bundleItems = cart.items.filter(item =>
        item.properties?.GroupId === groupId
      );

      if (bundleItems.length === 0) return null;

      const tlpItem = bundleItems.find(item => item.properties?.tlp === 'true');
      if (!tlpItem) return null;

      const requiredAddons = bundleItems.filter(item =>
        item.properties?.groupedProduct === 'true' &&
        item.properties?.tlp !== 'true' &&
        item.properties?.required === 'true'
      );

      const optionalAddons = bundleItems.filter(item =>
        item.properties?.groupedProduct === 'true' &&
        item.properties?.tlp !== 'true' &&
        item.properties?.required !== 'true'
      );

      const bespokeOptions = this.filterBespokeProperties(tlpItem.properties);

      return {
        title: tlpItem.product_title,
        variantTitle: tlpItem.variant_title,
        url: tlpItem.url,
        quantity: tlpItem.quantity,
        price: tlpItem.price,
        linePrice: tlpItem.line_price,
        bespokeOptions,
        requiredAddons: requiredAddons.map(item => ({
          title: item.product_title,
          variantTitle: item.variant_title,
          quantity: item.quantity,
          price: item.price,
          linePrice: item.line_price,
          bespokeOptions: this.filterBespokeProperties(item.properties)
        })),
        optionalAddons: optionalAddons.map(item => ({
          title: item.product_title,
          variantTitle: item.variant_title,
          quantity: item.quantity,
          price: item.price,
          linePrice: item.line_price,
          bespokeOptions: this.filterBespokeProperties(item.properties)
        })),
        currency: cart.currency
      };
    } catch (error) {
      console.error('Error collecting print data:', error);
      return null;
    }
  }

  /**
   * Handles full cart print action
   * Opens a new window with print-friendly cart summary
   */
  async handleCartPrint() {
    const cartData = await this.collectCartPrintData();
    if (!cartData) return;

    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) return;

    const html = this.buildCartPrintHtml(cartData);
    printWindow.document.write(html);
    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.print();
    };
  }

  /**
   * Collects full cart data for print
   * @returns {Promise<Object|null>} Cart print data object
   */
  async collectCartPrintData() {
    try {
      const cartResponse = await fetch('/cart.js', {
        headers: { 'Accept': 'application/json' }
      });
      const cart = await cartResponse.json();

      if (cart.items.length === 0) return null;

      const groupedItems = this.groupCartItems(cart.items);

      return {
        bundles: groupedItems.bundles,
        standaloneItems: groupedItems.standalone,
        itemCount: cart.item_count,
        subtotal: cart.original_total_price,
        totalDiscount: cart.total_discount,
        total: cart.total_price,
        currency: cart.currency
      };
    } catch (error) {
      console.error('Error collecting cart print data:', error);
      return null;
    }
  }

  /**
   * Groups cart items into bundles and standalone items
   * @param {Array} items - Cart items array
   * @returns {Object} Object with bundles and standalone arrays
   */
  groupCartItems(items) {
    const bundleGroups = {};
    const standalone = [];

    items.forEach(item => {
      const groupId = item.properties?.GroupId;

      if (groupId && item.properties?.groupedProduct === 'true') {
        if (!bundleGroups[groupId]) {
          bundleGroups[groupId] = {
            groupId,
            tlp: null,
            requiredAddons: [],
            optionalAddons: [],
            total: 0
          };
        }

        if (item.properties?.tlp === 'true') {
          bundleGroups[groupId].tlp = {
            title: item.product_title,
            variantTitle: item.variant_title,
            url: item.url,
            image: item.image,
            quantity: item.quantity,
            price: item.price,
            linePrice: item.line_price,
            bespokeOptions: this.filterBespokeProperties(item.properties)
          };
        } else if (item.properties?.required === 'true') {
          bundleGroups[groupId].requiredAddons.push({
            title: item.product_title,
            variantTitle: item.variant_title,
            image: item.image,
            quantity: item.quantity,
            price: item.price,
            linePrice: item.line_price,
            bespokeOptions: this.filterBespokeProperties(item.properties)
          });
        } else {
          bundleGroups[groupId].optionalAddons.push({
            title: item.product_title,
            variantTitle: item.variant_title,
            image: item.image,
            quantity: item.quantity,
            price: item.price,
            linePrice: item.line_price,
            bespokeOptions: this.filterBespokeProperties(item.properties)
          });
        }

        bundleGroups[groupId].total += item.line_price;
      } else if (!item.properties?.groupedProduct) {
        standalone.push({
          title: item.product_title,
          variantTitle: item.variant_title,
          url: item.url,
          image: item.image,
          quantity: item.quantity,
          price: item.price,
          linePrice: item.line_price,
          bespokeOptions: this.filterBespokeProperties(item.properties || {})
        });
      }
    });

    return {
      bundles: Object.values(bundleGroups).filter(bundle => bundle.tlp !== null),
      standalone
    };
  }

  /**
   * Builds print-friendly HTML for full cart
   * @param {Object} data - Cart print data
   * @returns {string} HTML string
   */
  buildCartPrintHtml(data) {
    const companyName = 'Direct Doors';
    const companyPhone = '01968 671 681';
    const companyEmail = 'info@directdoors.com';
    const companyUrl = 'www.directdoors.com';
    const contactParts = [companyPhone, companyEmail, companyUrl];

    const bundlesHtml = data.bundles.map((bundle, index) => this.buildBundleSectionHtml(bundle, index + 1, data.currency)).join('');

    const standaloneHtml = data.standaloneItems.length > 0
      ? `
        <div class="section">
          <h2>Additional Items</h2>
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Item</th>
                <th>Qty</th>
                <th>Price</th>
              </tr>
            </thead>
            <tbody>
              ${data.standaloneItems.map(item => {
                const hasOptions = Object.keys(item.bespokeOptions).length > 0;
                const optionsHtml = hasOptions
                  ? `<tr class="addon-options-row"><td colspan="4"><dl class="addon-options">${Object.entries(item.bespokeOptions).map(([key, value]) => `<span class="addon-option"><strong>${key}:</strong> ${value}</span>`).join('')}</dl></td></tr>`
                  : '';
                const imageHtml = item.image
                  ? `<img class="item-image" src="${item.image}" alt="">`
                  : '';
                return `
                <tr>
                  <td>${imageHtml}</td>
                  <td>${item.title}${item.variantTitle && item.variantTitle !== item.title ? ` - ${item.variantTitle}` : ''}</td>
                  <td>${item.quantity}</td>
                  <td>${this.formatPrice(item.linePrice, data.currency)}</td>
                </tr>
                ${optionsHtml}`;
              }).join('')}
            </tbody>
          </table>
        </div>
      `
      : '';

    const discountHtml = data.totalDiscount > 0
      ? `
        <div class="summary-row summary-row--discount">
          <span>Discount</span>
          <span>-${this.formatPrice(data.totalDiscount, data.currency)}</span>
        </div>
      `
      : '';

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${companyName} - Basket Summary</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 12pt;
            line-height: 1.5;
            padding: 2cm;
            color: #000;
          }
          .company-header { border-bottom: 2px solid #E61E26; margin-bottom: 2em; padding-bottom: 1em; }
          .company-logo { height: 30px; margin-bottom: 0.5em; width: auto; }
          .company-contact { font-size: 9pt; }
          .company-contact span:not(:last-child)::after { content: ' | '; }
          h1 { font-size: 18pt; margin-bottom: 1em; }
          h2 { font-size: 14pt; margin-bottom: 0.5em; border-bottom: 2px solid #E61E26; padding-bottom: 0.25em; }
          h3 { font-size: 12pt; margin-bottom: 0.5em; border-bottom: 1px solid #ddd; padding-bottom: 0.25em; }
          .bundle { margin-bottom: 2em; page-break-inside: avoid; }
          .bundle-header { margin-bottom: 1em; }
          .bundle-title { font-size: 14pt; margin-bottom: 0.25em; }
          .bundle-variant { font-size: 11pt; color: #666; font-weight: normal; }
          .meta { font-size: 10pt; margin-bottom: 0.5em; }
          .section { margin-bottom: 1.5em; }
          .options { display: grid; grid-template-columns: 1fr 1fr; gap: 0.5em 2em; }
          .option { display: flex; gap: 0.5em; }
          .option dt::after { content: ':'; }
          .option dd { font-weight: 600; }
          table { width: 100%; border-collapse: collapse; font-size: 11pt; }
          th, td { padding: 0.5em; text-align: left; border-bottom: 1px solid #eee; }
          th { font-weight: 600; background: #f9f9f9; }
          td:last-child, th:last-child { text-align: right; }
          .addon-options-row td { border-bottom: 1px solid #ddd; padding-top: 0; }
          .addon-options { display: flex; flex-wrap: wrap; font-size: 10pt; gap: 0.5em 1.5em; margin: 0; }
          .addon-option { white-space: nowrap; }
          .addon-option strong { font-weight: 500; }
          .bundle-total { margin-top: 0.5em; padding-top: 0.5em; border-top: 1px solid #ddd; font-size: 12pt; display: flex; justify-content: space-between; }
          .cart-summary { margin-top: 2em; padding-top: 1em; border-top: 2px solid #000; }
          .summary-row { display: flex; justify-content: space-between; font-size: 12pt; padding: 0.25em 0; }
          .summary-row--discount { color: #E61E26; }
          .summary-row--total { font-size: 16pt; font-weight: 700; margin-top: 0.5em; padding-top: 0.5em; border-top: 1px solid #ddd; }
          .disclaimer { border-top: 1px solid #ddd; font-size: 9pt; margin-top: 2em; padding-top: 1em; }
          .bundle-header { display: flex; gap: 1em; align-items: flex-start; }
          .bundle-image { flex-shrink: 0; width: 80px; height: 80px; object-fit: contain; background: #f9f9f9; border-radius: 4px; }
          .bundle-info { flex: 1; }
          .item-image { width: 50px; height: 50px; object-fit: contain; background: #f9f9f9; border-radius: 4px; vertical-align: middle; }
          @media print {
            body { padding: 0; orphans: 1; widows: 1; }
            @page { margin: 1.5cm; }
            * { break-before: auto; break-after: auto; }
          }
        </style>
      </head>
      <body>
        <div class="company-header">
          <svg class="company-logo" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 537 50" aria-hidden="true">
            <g clip-path="url(#clip0_cart)">
              <path d="M7.90029 0H30.4307C34.8929 0 38.4042 0.585651 40.9644 1.83016C43.5247 3.07467 45.4998 4.75842 46.8897 7.02782C48.2795 9.29722 49.1573 11.9326 49.5231 14.9341C49.8157 17.9356 49.7425 21.0835 49.2305 24.451C48.3527 29.7218 47.1091 33.7482 45.4998 36.6764C43.8173 39.6047 41.7691 42.0205 39.3551 43.9239C36.9412 45.9004 34.454 47.2182 31.8937 47.8038C28.4557 48.6823 25.3102 49.1215 22.6036 49.1215H0L7.90029 0ZM21.2869 11.1274L16.971 37.9941H20.7017C23.8472 37.9941 26.188 37.6281 27.651 36.9692C29.114 36.2372 30.3576 35.0659 31.3817 33.3089C32.4058 31.552 33.2836 28.6969 33.8688 24.7438C34.6735 19.5461 34.454 15.959 33.0642 14.0556C31.6743 12.1523 29.0409 11.2006 25.0907 11.2006H21.2869V11.1274ZM60.7152 0H74.3212L72.8582 9.29722H59.179L60.7152 0ZM58.5206 13.5432H72.1267L66.4209 49.1947H52.8149L58.5206 13.5432ZM80.9779 13.5432H93.7793L92.8284 19.3997C94.4377 16.8375 96.047 15.1537 97.4369 14.202C98.8999 13.2504 100.582 12.8111 102.484 12.8111C104.459 12.8111 106.581 13.47 108.775 14.7145L102.996 24.451C101.46 23.7921 100.29 23.4261 99.3388 23.4261C97.5832 23.4261 96.047 24.1581 94.8034 25.6223C93.0478 27.672 91.7311 31.6252 90.7801 37.2621L88.8782 49.1947H75.199L80.9779 13.5432ZM148.569 34.7731H121.211C121.138 36.9692 121.43 38.5798 122.235 39.6779C123.332 41.2152 124.868 42.0205 126.99 42.0205C128.307 42.0205 129.623 41.6545 130.94 40.9956C131.745 40.5564 132.622 39.8243 133.646 38.7262L146.887 39.9707C144.253 43.5578 141.327 46.1201 138.182 47.6574C135.036 49.1947 130.72 50 125.307 50C120.626 50 116.968 49.3411 114.481 48.0234C111.994 46.7057 110.092 44.5827 108.775 41.6545C107.459 38.7262 107.093 35.3587 107.751 31.4056C108.629 25.8419 111.189 21.3031 115.286 17.8624C119.382 14.4217 124.576 12.6647 130.867 12.6647C135.987 12.6647 139.864 13.47 142.571 15.0073C145.277 16.5447 147.179 18.8141 148.204 21.7423C149.301 24.6706 149.447 28.4773 148.642 33.1625L148.569 34.7731ZM135.695 28.1845C135.841 25.549 135.475 23.6457 134.451 22.5476C133.5 21.3763 132.037 20.8638 130.208 20.8638C128.014 20.8638 126.185 21.7423 124.576 23.4261C123.552 24.5242 122.82 26.0615 122.235 28.2577H135.695V28.1845ZM181.268 35.2123L193.996 36.6764C192.826 39.3851 191.289 41.7277 189.314 43.7042C187.339 45.6808 184.998 47.2182 182.292 48.3162C179.585 49.4143 176.293 49.9268 172.343 49.9268C168.539 49.9268 165.467 49.5608 163.053 48.9019C160.639 48.1698 158.664 47.0717 157.128 45.4612C155.592 43.8507 154.421 42.0205 153.763 39.8975C153.105 37.7745 153.031 34.9927 153.617 31.4788C154.202 27.8184 155.299 24.7438 156.982 22.328C158.152 20.571 159.688 18.9605 161.517 17.4963C163.346 16.1054 165.101 15.0073 166.93 14.3485C169.783 13.2504 173.294 12.7379 177.391 12.7379C183.17 12.7379 187.485 13.7628 190.192 15.8858C192.899 17.9356 194.581 21.0102 195.166 25.0366L182.072 26.7204C181.926 25.183 181.341 24.0849 180.463 23.2796C179.512 22.4744 178.195 22.1083 176.367 22.1083C174.099 22.1083 172.124 22.9136 170.515 24.5242C168.832 26.1347 167.735 28.6237 167.223 31.8448C166.784 34.7731 167.076 36.9692 168.247 38.5066C169.417 40.0439 171.027 40.776 173.148 40.776C174.904 40.776 176.513 40.3367 177.903 39.3851C179.219 38.5066 180.39 37.1157 181.268 35.2123ZM222.671 0L220.476 13.5432H228.011L226.402 23.5725H218.867L216.819 36.164C216.6 37.7013 216.526 38.653 216.746 39.1654C217.038 39.8975 217.77 40.3367 218.94 40.3367C219.964 40.3367 221.427 40.0439 223.329 39.4583L222.817 48.9019C219.306 49.634 216.087 50 213.161 50C209.796 50 207.309 49.5608 205.846 48.6823C204.383 47.8038 203.432 46.4861 202.92 44.7291C202.408 42.9722 202.481 40.0439 203.14 36.0908L205.188 23.5725H200.141L201.75 13.5432H206.797L207.821 7.02782L222.671 0Z" fill="#E61E26"/>
              <path d="M241.617 0H264.148C268.61 0 272.194 0.585651 274.901 1.83016C277.607 3.07467 279.875 4.75842 281.704 7.02782C283.459 9.29722 284.776 11.9326 285.581 14.9341C286.385 17.9356 286.751 21.0835 286.751 24.451C286.751 29.7218 286.166 33.7482 284.922 36.6764C283.752 39.6047 282.069 42.0205 279.948 43.9239C277.827 45.9004 275.559 47.2182 273.145 47.8038C269.78 48.6823 266.781 49.1215 264.074 49.1215H241.544V0H241.617ZM256.759 11.1274V37.9941H260.49C263.635 37.9941 265.903 37.6281 267.293 36.9692C268.61 36.2372 269.707 35.0659 270.439 33.3089C271.17 31.552 271.609 28.6969 271.609 24.7438C271.609 19.5461 270.731 15.959 269.049 14.0556C267.366 12.1523 264.513 11.2006 260.636 11.2006H256.759V11.1274ZM292.237 31.4788C292.237 26.0615 294.066 21.5959 297.724 18.082C301.381 14.5681 306.356 12.8111 312.573 12.8111C319.669 12.8111 325.082 14.8609 328.74 19.0337C331.666 22.4012 333.129 26.5007 333.129 31.3324C333.129 36.8228 331.3 41.2884 327.716 44.8023C324.058 48.3162 319.084 50.0732 312.647 50.0732C306.941 50.0732 302.332 48.6091 298.748 45.6808C294.359 42.0205 292.237 37.2621 292.237 31.4788ZM305.917 31.4056C305.917 34.5534 306.575 36.896 307.819 38.4334C309.135 39.9707 310.745 40.7028 312.647 40.7028C314.622 40.7028 316.231 39.9707 317.474 38.4334C318.718 36.9692 319.376 34.5534 319.376 31.2591C319.376 28.1845 318.718 25.8419 317.474 24.3777C316.231 22.8404 314.622 22.1083 312.72 22.1083C310.745 22.1083 309.062 22.8404 307.819 24.3777C306.575 25.9883 305.917 28.3309 305.917 31.4056ZM338.03 31.4788C338.03 26.0615 339.859 21.5959 343.516 18.082C347.174 14.5681 352.148 12.8111 358.366 12.8111C365.461 12.8111 370.875 14.8609 374.532 19.0337C377.458 22.4012 378.921 26.5007 378.921 31.3324C378.921 36.8228 377.092 41.2884 373.508 44.8023C369.85 48.3162 364.876 50.0732 358.439 50.0732C352.733 50.0732 348.125 48.6091 344.54 45.6808C340.151 42.0205 338.03 37.2621 338.03 31.4788ZM351.636 31.4056C351.636 34.5534 352.294 36.896 353.538 38.4334C354.855 39.9707 356.464 40.7028 358.366 40.7028C360.341 40.7028 361.95 39.9707 363.194 38.4334C364.437 36.9692 365.096 34.5534 365.096 31.2591C365.096 28.1845 364.437 25.8419 363.194 24.3777C361.95 22.8404 360.341 22.1083 358.439 22.1083C356.464 22.1083 354.781 22.8404 353.538 24.3777C352.294 25.9883 351.636 28.3309 351.636 31.4056ZM385.578 13.5432H398.379V19.3997C399.623 16.8375 400.866 15.1537 402.183 14.202C403.5 13.2504 405.109 12.8111 407.011 12.8111C408.986 12.8111 411.254 13.47 413.595 14.7145L409.352 24.451C407.743 23.7921 406.499 23.4261 405.548 23.4261C403.792 23.4261 402.403 24.1581 401.378 25.6223C399.989 27.672 399.257 31.6252 399.257 37.2621V49.1947H385.578V13.5432ZM417.179 39.2386L430.712 37.9941C431.297 39.6047 432.029 40.776 433.053 41.4348C434.077 42.0937 435.394 42.4597 437.076 42.4597C438.905 42.4597 440.295 42.0937 441.319 41.2884C442.123 40.7028 442.489 39.9707 442.489 39.0922C442.489 38.1406 441.977 37.3353 440.953 36.8228C440.222 36.4568 438.246 35.9444 435.101 35.4319C430.346 34.6266 427.054 33.8214 425.226 33.1625C423.397 32.4305 421.861 31.2591 420.544 29.5754C419.3 27.8917 418.642 25.9883 418.642 23.8653C418.642 21.5227 419.3 19.4729 420.69 17.7892C422.08 16.1054 423.909 14.7877 426.323 13.9824C428.737 13.1772 431.955 12.7379 435.906 12.7379C440.148 12.7379 443.294 13.0307 445.342 13.6896C447.39 14.3485 449.073 15.3734 450.389 16.6911C451.706 18.082 452.877 19.9122 453.754 22.2548L440.807 23.4993C440.441 22.328 439.929 21.5227 439.124 21.0102C438.027 20.2782 436.783 19.9122 435.247 19.9122C433.711 19.9122 432.614 20.205 431.882 20.7174C431.151 21.2299 430.858 21.9619 430.858 22.694C430.858 23.5725 431.297 24.2313 432.175 24.6706C433.053 25.1098 435.028 25.549 438.027 25.8419C442.562 26.3543 445.927 27.0864 448.122 27.9649C450.316 28.9165 451.999 30.1611 453.169 31.918C454.34 33.6018 454.925 35.5051 454.925 37.4817C454.925 39.5315 454.34 41.508 453.096 43.4846C451.853 45.4612 449.877 46.9985 447.244 48.0966C444.611 49.2679 440.953 49.8536 436.345 49.8536C429.834 49.8536 425.226 48.9019 422.446 47.0717C419.666 45.3148 417.911 42.6794 417.179 39.2386ZM461.728 48.9019L462.167 44.4363H457.412L456.973 48.9019H461.728ZM470.725 40.0439C471.311 39.3851 472.115 39.0922 472.993 39.0922C473.725 39.0922 474.237 39.2387 474.676 39.5315C475.041 39.8243 475.261 40.1903 475.407 40.7028L479.65 40.1171C479.357 38.8726 478.699 37.9209 477.821 37.1889C476.87 36.4568 475.334 36.0908 473.286 36.0908C471.823 36.0908 470.579 36.3104 469.628 36.6764C469.043 36.896 468.385 37.2621 467.799 37.7745C467.214 38.287 466.702 38.7994 466.336 39.4583C465.824 40.3367 465.459 41.3616 465.385 42.6794C465.239 43.9239 465.312 44.8755 465.605 45.6076C465.897 46.3397 466.336 46.9985 466.922 47.511C467.507 48.0234 468.238 48.4627 469.043 48.6823C469.921 48.9019 471.018 49.0483 472.335 49.0483C473.725 49.0483 474.895 48.8287 475.773 48.4627C476.724 48.0966 477.455 47.511 478.114 46.8521C478.699 46.2665 479.138 45.5344 479.503 44.6559L475.334 44.1435C475.041 44.8023 474.676 45.3148 474.163 45.6076C473.651 45.9736 473.066 46.1201 472.335 46.1201C471.457 46.1201 470.799 45.8272 470.287 45.2416C469.774 44.6559 469.628 43.7774 469.701 42.6794C469.774 41.5813 470.14 40.6296 470.725 40.0439ZM489.525 36.164C487.331 36.164 485.502 36.8228 484.112 37.9941C482.722 39.2387 481.917 40.776 481.698 42.6794C481.479 44.7291 482.064 46.3397 483.454 47.5842C484.551 48.6091 486.16 49.1215 488.135 49.1215C490.403 49.1215 492.232 48.5359 493.622 47.2914C495.011 46.0468 495.816 44.5095 495.962 42.6061C496.109 40.9224 495.743 39.4583 494.865 38.3602C493.841 36.896 492.012 36.164 489.525 36.164ZM491.573 42.6794C491.427 43.9239 491.134 44.8755 490.549 45.4612C489.964 46.0468 489.306 46.3397 488.574 46.3397C487.77 46.3397 487.184 46.0468 486.745 45.388C486.307 44.8023 486.16 43.8507 486.233 42.6794C486.38 41.508 486.672 40.6296 487.257 40.0439C487.843 39.4583 488.501 39.1654 489.306 39.1654C490.037 39.1654 490.696 39.4583 491.134 40.1171C491.573 40.6296 491.72 41.508 491.573 42.6794ZM516.81 36.164C515.933 36.164 515.128 36.3104 514.47 36.6032C513.811 36.896 513.08 37.4817 512.275 38.287L512.056 38.5066L511.909 38.2138C511.617 37.5549 511.178 37.0425 510.666 36.6764C510.154 36.3104 509.422 36.164 508.398 36.164C507.52 36.164 506.716 36.3104 505.984 36.6032C505.326 36.896 504.594 37.4817 503.863 38.2138L503.351 38.7262L503.57 36.3836H499.474L498.23 48.7555H502.692L503.351 42.3865C503.424 41.3616 503.716 40.6296 504.155 40.1171C504.594 39.6047 505.179 39.3851 505.765 39.3851C506.204 39.3851 506.496 39.5315 506.789 39.7511C507.081 39.9707 507.228 40.2635 507.374 40.7028C507.447 40.9224 507.447 41.3616 507.374 41.9473L506.716 48.7555H511.178L511.836 42.3133C511.909 41.2884 512.202 40.6296 512.641 40.1171C513.08 39.6779 513.665 39.3851 514.25 39.3851C514.835 39.3851 515.274 39.6779 515.64 40.1903C515.859 40.4832 515.933 40.9956 515.859 41.6545L515.128 48.7555H519.59L520.395 40.8492C520.541 39.1654 520.322 37.9209 519.663 37.1889C519.078 36.6032 518.127 36.164 516.81 36.164Z" fill="#050505"/>
              <path d="M537 27.4524C537 31.918 533.489 35.5051 528.88 35.5051C524.345 35.5051 520.688 31.9912 520.688 27.4524C520.688 23.0601 524.272 19.5461 528.88 19.5461C533.416 19.5461 537 23.0601 537 27.4524ZM522.736 27.4524C522.736 30.9663 525.369 33.7482 528.88 33.7482C532.392 33.7482 534.879 30.9663 534.879 27.4524C534.879 23.9385 532.319 21.0835 528.807 21.0835C525.296 21.0835 522.736 23.9385 522.736 27.4524ZM527.637 31.6252H525.808V23.6457C526.54 23.4993 527.564 23.4261 528.88 23.4261C530.343 23.4261 531.075 23.6457 531.66 24.0117C532.099 24.3778 532.465 24.9634 532.465 25.7687C532.465 26.6472 531.806 27.306 530.855 27.5989V27.6721C531.66 27.9649 532.099 28.5505 532.318 29.5754C532.538 30.7467 532.684 31.2592 532.904 31.552H530.929C530.709 31.2592 530.563 30.5271 530.27 29.6486C530.124 28.7702 529.612 28.4041 528.661 28.4041H527.783V31.6252H527.637ZM527.637 27.0864H528.515C529.539 27.0864 530.343 26.7204 530.343 25.9151C530.343 25.183 529.831 24.7438 528.661 24.7438C528.149 24.7438 527.856 24.817 527.637 24.817V27.0864Z" fill="#050505"/>
            </g>
            <defs><clipPath id="clip0_cart"><rect width="537" height="50" fill="white"/></clipPath></defs>
          </svg>
          <div class="company-contact">
            ${contactParts.map(part => `<span>${part}</span>`).join('')}
          </div>
        </div>

        <h1>Basket Summary</h1>

        ${bundlesHtml}
        ${standaloneHtml}

        <div class="cart-summary">
          <div class="summary-row">
            <span>Subtotal</span>
            <span>${this.formatPrice(data.subtotal, data.currency)}</span>
          </div>
          ${discountHtml}
          <div class="summary-row summary-row--total">
            <span>Total</span>
            <span>${this.formatPrice(data.total, data.currency)}</span>
          </div>
        </div>

        <p class="disclaimer">This basket summary is for reference only and does not constitute a quotation. Prices shown are indicative and may be subject to change. Please confirm final pricing at checkout.</p>
      </body>
      </html>
    `;
  }

  /**
   * Builds HTML section for a single bundle in cart print
   * @param {Object} bundle - Bundle data
   * @param {number} index - Bundle number
   * @param {string} currency - Currency code
   * @returns {string} HTML string
   */
  buildBundleSectionHtml(bundle, index, currency) {
    const bespokeHtml = Object.keys(bundle.tlp.bespokeOptions).length > 0
      ? `
        <div class="section">
          <h3>Configuration</h3>
          <dl class="options">
            ${Object.entries(bundle.tlp.bespokeOptions).map(([key, value]) => `
              <div class="option">
                <dt>${key}</dt>
                <dd>${value}</dd>
              </div>
            `).join('')}
          </dl>
        </div>
      `
      : '';

    const requiredAddonsHtml = bundle.requiredAddons.length > 0
      ? `
        <div class="section">
          <h3>Required Add-ons</h3>
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Item</th>
                <th>Qty</th>
                <th>Price</th>
              </tr>
            </thead>
            <tbody>
              ${bundle.requiredAddons.map(addon => {
                const hasOptions = Object.keys(addon.bespokeOptions).length > 0;
                const optionsHtml = hasOptions
                  ? `<tr class="addon-options-row"><td colspan="4"><dl class="addon-options">${Object.entries(addon.bespokeOptions).map(([key, value]) => `<span class="addon-option"><strong>${key}:</strong> ${value}</span>`).join('')}</dl></td></tr>`
                  : '';
                const addonImageHtml = addon.image
                  ? `<img class="item-image" src="${addon.image}" alt="">`
                  : '';
                return `
                <tr>
                  <td>${addonImageHtml}</td>
                  <td>${addon.title}${addon.variantTitle && addon.variantTitle !== addon.title ? ` - ${addon.variantTitle}` : ''}</td>
                  <td>${addon.quantity}</td>
                  <td>${addon.price > 0 ? this.formatPrice(addon.linePrice, currency) : 'Included'}</td>
                </tr>
                ${optionsHtml}`;
              }).join('')}
            </tbody>
          </table>
        </div>
      `
      : '';

    const optionalAddonsHtml = bundle.optionalAddons.length > 0
      ? `
        <div class="section">
          <h3>Optional Add-ons</h3>
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Item</th>
                <th>Qty</th>
                <th>Price</th>
              </tr>
            </thead>
            <tbody>
              ${bundle.optionalAddons.map(addon => {
                const hasOptions = Object.keys(addon.bespokeOptions).length > 0;
                const optionsHtml = hasOptions
                  ? `<tr class="addon-options-row"><td colspan="4"><dl class="addon-options">${Object.entries(addon.bespokeOptions).map(([key, value]) => `<span class="addon-option"><strong>${key}:</strong> ${value}</span>`).join('')}</dl></td></tr>`
                  : '';
                const addonImageHtml = addon.image
                  ? `<img class="item-image" src="${addon.image}" alt="">`
                  : '';
                return `
                <tr>
                  <td>${addonImageHtml}</td>
                  <td>${addon.title}${addon.variantTitle && addon.variantTitle !== addon.title ? ` - ${addon.variantTitle}` : ''}</td>
                  <td>${addon.quantity}</td>
                  <td>${this.formatPrice(addon.linePrice, currency)}</td>
                </tr>
                ${optionsHtml}`;
              }).join('')}
            </tbody>
          </table>
        </div>
      `
      : '';

    const imageHtml = bundle.tlp.image
      ? `<img class="bundle-image" src="${bundle.tlp.image}" alt="">`
      : '';

    return `
      <div class="bundle">
        <div class="bundle-header">
          ${imageHtml}
          <div class="bundle-info">
            <h2>
              ${bundle.tlp.title}
              ${bundle.tlp.variantTitle && bundle.tlp.variantTitle !== bundle.tlp.title ? `<span class="bundle-variant"> - ${bundle.tlp.variantTitle}</span>` : ''}
            </h2>
            <p class="meta">Quantity: ${bundle.tlp.quantity} &bull; Unit Price: ${this.formatPrice(bundle.tlp.price, currency)}</p>
          </div>
        </div>

        ${bespokeHtml}
        ${requiredAddonsHtml}
        ${optionalAddonsHtml}

        <div class="bundle-total">
          <span>Bundle Total</span>
          <span>${this.formatPrice(bundle.total, currency)}</span>
        </div>
      </div>
    `;
  }

  /**
   * Builds print-friendly HTML for single bundle
   * @param {Object} data - Bundle print data
   * @returns {string} HTML string
   */
  buildPrintHtml(data) {
    const bespokeHtml = Object.keys(data.bespokeOptions).length > 0
      ? `
        <div class="section">
          <h3>Configuration</h3>
          <dl class="options">
            ${Object.entries(data.bespokeOptions).map(([key, value]) => `
              <div class="option">
                <dt>${key}</dt>
                <dd>${value}</dd>
              </div>
            `).join('')}
          </dl>
        </div>
      `
      : '';

    const requiredAddonsHtml = data.requiredAddons.length > 0
      ? `
        <div class="section">
          <h3>Required Add-ons</h3>
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Qty</th>
                <th>Price</th>
              </tr>
            </thead>
            <tbody>
              ${data.requiredAddons.map(addon => {
                const hasOptions = Object.keys(addon.bespokeOptions).length > 0;
                const optionsHtml = hasOptions
                  ? `<tr class="addon-options-row"><td colspan="3"><dl class="addon-options">${Object.entries(addon.bespokeOptions).map(([key, value]) => `<span class="addon-option"><strong>${key}:</strong> ${value}</span>`).join('')}</dl></td></tr>`
                  : '';
                return `
                <tr>
                  <td>${addon.title}${addon.variantTitle && addon.variantTitle !== addon.title ? ` - ${addon.variantTitle}` : ''}</td>
                  <td>${addon.quantity}</td>
                  <td>${addon.price > 0 ? this.formatPrice(addon.linePrice, data.currency) : 'Included'}</td>
                </tr>
                ${optionsHtml}`;
              }).join('')}
            </tbody>
          </table>
        </div>
      `
      : '';

    const optionalAddonsHtml = data.optionalAddons.length > 0
      ? `
        <div class="section">
          <h3>Optional Add-ons</h3>
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Qty</th>
                <th>Price</th>
              </tr>
            </thead>
            <tbody>
              ${data.optionalAddons.map(addon => {
                const hasOptions = Object.keys(addon.bespokeOptions).length > 0;
                const optionsHtml = hasOptions
                  ? `<tr class="addon-options-row"><td colspan="3"><dl class="addon-options">${Object.entries(addon.bespokeOptions).map(([key, value]) => `<span class="addon-option"><strong>${key}:</strong> ${value}</span>`).join('')}</dl></td></tr>`
                  : '';
                return `
                <tr>
                  <td>${addon.title}${addon.variantTitle && addon.variantTitle !== addon.title ? ` - ${addon.variantTitle}` : ''}</td>
                  <td>${addon.quantity}</td>
                  <td>${this.formatPrice(addon.linePrice, data.currency)}</td>
                </tr>
                ${optionsHtml}`;
              }).join('')}
            </tbody>
          </table>
        </div>
      `
      : '';

    const totalPrice = data.linePrice +
      data.requiredAddons.reduce((sum, a) => sum + a.linePrice, 0) +
      data.optionalAddons.reduce((sum, a) => sum + a.linePrice, 0);

    const companyName = 'Direct Doors';
    const companyPhone = '01968 671 681';
    const companyEmail = 'info@directdoors.com';
    const companyUrl = 'www.directdoors.com';
    const contactParts = [companyPhone, companyEmail, companyUrl];

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${companyName} - ${data.title} Configuration</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 12pt;
            line-height: 1.5;
            padding: 2cm;
            color: #000;
          }
          .company-header { border-bottom: 2px solid #E61E26; margin-bottom: 2em; padding-bottom: 1em; }
          .company-logo { height: 30px; margin-bottom: 0.5em; width: auto; }
          .company-contact { font-size: 9pt; }
          .company-contact span:not(:last-child)::after { content: ' | '; }
          h1 { font-size: 18pt; margin-bottom: 0.5em; }
          h2 { font-size: 14pt; font-weight: normal; margin-bottom: 1em; }
          h3 { font-size: 12pt; margin-bottom: 0.5em; border-bottom: 1px solid #ddd; padding-bottom: 0.25em; }
          .product-header { margin-bottom: 2em; }
          .meta { font-size: 10pt; margin-bottom: 0.5em; }
          .section { margin-bottom: 1.5em; }
          .options { display: grid; grid-template-columns: 1fr 1fr; gap: 0.5em 2em; }
          .option { display: flex; gap: 0.5em; }
          .option dt::after { content: ':'; }
          .option dd { font-weight: 600; }
          table { width: 100%; border-collapse: collapse; font-size: 11pt; }
          th, td { padding: 0.5em; text-align: left; border-bottom: 1px solid #eee; }
          th { font-weight: 600; background: #f9f9f9; }
          td:last-child, th:last-child { text-align: right; }
          .addon-options-row td { border-bottom: 1px solid #ddd; padding-top: 0; }
          .addon-options { display: flex; flex-wrap: wrap; font-size: 10pt; gap: 0.5em 1.5em; margin: 0; }
          .addon-option { white-space: nowrap; }
          .addon-option strong { font-weight: 500; }
          .total { margin-top: 1em; padding-top: 1em; border-top: 2px solid #000; font-size: 14pt; display: flex; justify-content: space-between; }
          .total-label { font-weight: 600; }
          .total-value { font-weight: 700; }
          .disclaimer { border-top: 1px solid #ddd; font-size: 9pt; margin-top: 2em; padding-top: 1em; }
          @media print {
            body { padding: 0; }
            @page { margin: 2cm; }
          }
        </style>
      </head>
      <body>
        <div class="company-header">
          <svg class="company-logo" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 537 50" aria-hidden="true">
            <g clip-path="url(#clip0_print)">
              <path d="M7.90029 0H30.4307C34.8929 0 38.4042 0.585651 40.9644 1.83016C43.5247 3.07467 45.4998 4.75842 46.8897 7.02782C48.2795 9.29722 49.1573 11.9326 49.5231 14.9341C49.8157 17.9356 49.7425 21.0835 49.2305 24.451C48.3527 29.7218 47.1091 33.7482 45.4998 36.6764C43.8173 39.6047 41.7691 42.0205 39.3551 43.9239C36.9412 45.9004 34.454 47.2182 31.8937 47.8038C28.4557 48.6823 25.3102 49.1215 22.6036 49.1215H0L7.90029 0ZM21.2869 11.1274L16.971 37.9941H20.7017C23.8472 37.9941 26.188 37.6281 27.651 36.9692C29.114 36.2372 30.3576 35.0659 31.3817 33.3089C32.4058 31.552 33.2836 28.6969 33.8688 24.7438C34.6735 19.5461 34.454 15.959 33.0642 14.0556C31.6743 12.1523 29.0409 11.2006 25.0907 11.2006H21.2869V11.1274ZM60.7152 0H74.3212L72.8582 9.29722H59.179L60.7152 0ZM58.5206 13.5432H72.1267L66.4209 49.1947H52.8149L58.5206 13.5432ZM80.9779 13.5432H93.7793L92.8284 19.3997C94.4377 16.8375 96.047 15.1537 97.4369 14.202C98.8999 13.2504 100.582 12.8111 102.484 12.8111C104.459 12.8111 106.581 13.47 108.775 14.7145L102.996 24.451C101.46 23.7921 100.29 23.4261 99.3388 23.4261C97.5832 23.4261 96.047 24.1581 94.8034 25.6223C93.0478 27.672 91.7311 31.6252 90.7801 37.2621L88.8782 49.1947H75.199L80.9779 13.5432ZM148.569 34.7731H121.211C121.138 36.9692 121.43 38.5798 122.235 39.6779C123.332 41.2152 124.868 42.0205 126.99 42.0205C128.307 42.0205 129.623 41.6545 130.94 40.9956C131.745 40.5564 132.622 39.8243 133.646 38.7262L146.887 39.9707C144.253 43.5578 141.327 46.1201 138.182 47.6574C135.036 49.1947 130.72 50 125.307 50C120.626 50 116.968 49.3411 114.481 48.0234C111.994 46.7057 110.092 44.5827 108.775 41.6545C107.459 38.7262 107.093 35.3587 107.751 31.4056C108.629 25.8419 111.189 21.3031 115.286 17.8624C119.382 14.4217 124.576 12.6647 130.867 12.6647C135.987 12.6647 139.864 13.47 142.571 15.0073C145.277 16.5447 147.179 18.8141 148.204 21.7423C149.301 24.6706 149.447 28.4773 148.642 33.1625L148.569 34.7731ZM135.695 28.1845C135.841 25.549 135.475 23.6457 134.451 22.5476C133.5 21.3763 132.037 20.8638 130.208 20.8638C128.014 20.8638 126.185 21.7423 124.576 23.4261C123.552 24.5242 122.82 26.0615 122.235 28.2577H135.695V28.1845ZM181.268 35.2123L193.996 36.6764C192.826 39.3851 191.289 41.7277 189.314 43.7042C187.339 45.6808 184.998 47.2182 182.292 48.3162C179.585 49.4143 176.293 49.9268 172.343 49.9268C168.539 49.9268 165.467 49.5608 163.053 48.9019C160.639 48.1698 158.664 47.0717 157.128 45.4612C155.592 43.8507 154.421 42.0205 153.763 39.8975C153.105 37.7745 153.031 34.9927 153.617 31.4788C154.202 27.8184 155.299 24.7438 156.982 22.328C158.152 20.571 159.688 18.9605 161.517 17.4963C163.346 16.1054 165.101 15.0073 166.93 14.3485C169.783 13.2504 173.294 12.7379 177.391 12.7379C183.17 12.7379 187.485 13.7628 190.192 15.8858C192.899 17.9356 194.581 21.0102 195.166 25.0366L182.072 26.7204C181.926 25.183 181.341 24.0849 180.463 23.2796C179.512 22.4744 178.195 22.1083 176.367 22.1083C174.099 22.1083 172.124 22.9136 170.515 24.5242C168.832 26.1347 167.735 28.6237 167.223 31.8448C166.784 34.7731 167.076 36.9692 168.247 38.5066C169.417 40.0439 171.027 40.776 173.148 40.776C174.904 40.776 176.513 40.3367 177.903 39.3851C179.219 38.5066 180.39 37.1157 181.268 35.2123ZM222.671 0L220.476 13.5432H228.011L226.402 23.5725H218.867L216.819 36.164C216.6 37.7013 216.526 38.653 216.746 39.1654C217.038 39.8975 217.77 40.3367 218.94 40.3367C219.964 40.3367 221.427 40.0439 223.329 39.4583L222.817 48.9019C219.306 49.634 216.087 50 213.161 50C209.796 50 207.309 49.5608 205.846 48.6823C204.383 47.8038 203.432 46.4861 202.92 44.7291C202.408 42.9722 202.481 40.0439 203.14 36.0908L205.188 23.5725H200.141L201.75 13.5432H206.797L207.821 7.02782L222.671 0Z" fill="#E61E26"/>
              <path d="M241.617 0H264.148C268.61 0 272.194 0.585651 274.901 1.83016C277.607 3.07467 279.875 4.75842 281.704 7.02782C283.459 9.29722 284.776 11.9326 285.581 14.9341C286.385 17.9356 286.751 21.0835 286.751 24.451C286.751 29.7218 286.166 33.7482 284.922 36.6764C283.752 39.6047 282.069 42.0205 279.948 43.9239C277.827 45.9004 275.559 47.2182 273.145 47.8038C269.78 48.6823 266.781 49.1215 264.074 49.1215H241.544V0H241.617ZM256.759 11.1274V37.9941H260.49C263.635 37.9941 265.903 37.6281 267.293 36.9692C268.61 36.2372 269.707 35.0659 270.439 33.3089C271.17 31.552 271.609 28.6969 271.609 24.7438C271.609 19.5461 270.731 15.959 269.049 14.0556C267.366 12.1523 264.513 11.2006 260.636 11.2006H256.759V11.1274ZM292.237 31.4788C292.237 26.0615 294.066 21.5959 297.724 18.082C301.381 14.5681 306.356 12.8111 312.573 12.8111C319.669 12.8111 325.082 14.8609 328.74 19.0337C331.666 22.4012 333.129 26.5007 333.129 31.3324C333.129 36.8228 331.3 41.2884 327.716 44.8023C324.058 48.3162 319.084 50.0732 312.647 50.0732C306.941 50.0732 302.332 48.6091 298.748 45.6808C294.359 42.0205 292.237 37.2621 292.237 31.4788ZM305.917 31.4056C305.917 34.5534 306.575 36.896 307.819 38.4334C309.135 39.9707 310.745 40.7028 312.647 40.7028C314.622 40.7028 316.231 39.9707 317.474 38.4334C318.718 36.9692 319.376 34.5534 319.376 31.2591C319.376 28.1845 318.718 25.8419 317.474 24.3777C316.231 22.8404 314.622 22.1083 312.72 22.1083C310.745 22.1083 309.062 22.8404 307.819 24.3777C306.575 25.9883 305.917 28.3309 305.917 31.4056ZM338.03 31.4788C338.03 26.0615 339.859 21.5959 343.516 18.082C347.174 14.5681 352.148 12.8111 358.366 12.8111C365.461 12.8111 370.875 14.8609 374.532 19.0337C377.458 22.4012 378.921 26.5007 378.921 31.3324C378.921 36.8228 377.092 41.2884 373.508 44.8023C369.85 48.3162 364.876 50.0732 358.439 50.0732C352.733 50.0732 348.125 48.6091 344.54 45.6808C340.151 42.0205 338.03 37.2621 338.03 31.4788ZM351.636 31.4056C351.636 34.5534 352.294 36.896 353.538 38.4334C354.855 39.9707 356.464 40.7028 358.366 40.7028C360.341 40.7028 361.95 39.9707 363.194 38.4334C364.437 36.9692 365.096 34.5534 365.096 31.2591C365.096 28.1845 364.437 25.8419 363.194 24.3777C361.95 22.8404 360.341 22.1083 358.439 22.1083C356.464 22.1083 354.781 22.8404 353.538 24.3777C352.294 25.9883 351.636 28.3309 351.636 31.4056ZM385.578 13.5432H398.379V19.3997C399.623 16.8375 400.866 15.1537 402.183 14.202C403.5 13.2504 405.109 12.8111 407.011 12.8111C408.986 12.8111 411.254 13.47 413.595 14.7145L409.352 24.451C407.743 23.7921 406.499 23.4261 405.548 23.4261C403.792 23.4261 402.403 24.1581 401.378 25.6223C399.989 27.672 399.257 31.6252 399.257 37.2621V49.1947H385.578V13.5432ZM417.179 39.2386L430.712 37.9941C431.297 39.6047 432.029 40.776 433.053 41.4348C434.077 42.0937 435.394 42.4597 437.076 42.4597C438.905 42.4597 440.295 42.0937 441.319 41.2884C442.123 40.7028 442.489 39.9707 442.489 39.0922C442.489 38.1406 441.977 37.3353 440.953 36.8228C440.222 36.4568 438.246 35.9444 435.101 35.4319C430.346 34.6266 427.054 33.8214 425.226 33.1625C423.397 32.4305 421.861 31.2591 420.544 29.5754C419.3 27.8917 418.642 25.9883 418.642 23.8653C418.642 21.5227 419.3 19.4729 420.69 17.7892C422.08 16.1054 423.909 14.7877 426.323 13.9824C428.737 13.1772 431.955 12.7379 435.906 12.7379C440.148 12.7379 443.294 13.0307 445.342 13.6896C447.39 14.3485 449.073 15.3734 450.389 16.6911C451.706 18.082 452.877 19.9122 453.754 22.2548L440.807 23.4993C440.441 22.328 439.929 21.5227 439.124 21.0102C438.027 20.2782 436.783 19.9122 435.247 19.9122C433.711 19.9122 432.614 20.205 431.882 20.7174C431.151 21.2299 430.858 21.9619 430.858 22.694C430.858 23.5725 431.297 24.2313 432.175 24.6706C433.053 25.1098 435.028 25.549 438.027 25.8419C442.562 26.3543 445.927 27.0864 448.122 27.9649C450.316 28.9165 451.999 30.1611 453.169 31.918C454.34 33.6018 454.925 35.5051 454.925 37.4817C454.925 39.5315 454.34 41.508 453.096 43.4846C451.853 45.4612 449.877 46.9985 447.244 48.0966C444.611 49.2679 440.953 49.8536 436.345 49.8536C429.834 49.8536 425.226 48.9019 422.446 47.0717C419.666 45.3148 417.911 42.6794 417.179 39.2386ZM461.728 48.9019L462.167 44.4363H457.412L456.973 48.9019H461.728ZM470.725 40.0439C471.311 39.3851 472.115 39.0922 472.993 39.0922C473.725 39.0922 474.237 39.2387 474.676 39.5315C475.041 39.8243 475.261 40.1903 475.407 40.7028L479.65 40.1171C479.357 38.8726 478.699 37.9209 477.821 37.1889C476.87 36.4568 475.334 36.0908 473.286 36.0908C471.823 36.0908 470.579 36.3104 469.628 36.6764C469.043 36.896 468.385 37.2621 467.799 37.7745C467.214 38.287 466.702 38.7994 466.336 39.4583C465.824 40.3367 465.459 41.3616 465.385 42.6794C465.239 43.9239 465.312 44.8755 465.605 45.6076C465.897 46.3397 466.336 46.9985 466.922 47.511C467.507 48.0234 468.238 48.4627 469.043 48.6823C469.921 48.9019 471.018 49.0483 472.335 49.0483C473.725 49.0483 474.895 48.8287 475.773 48.4627C476.724 48.0966 477.455 47.511 478.114 46.8521C478.699 46.2665 479.138 45.5344 479.503 44.6559L475.334 44.1435C475.041 44.8023 474.676 45.3148 474.163 45.6076C473.651 45.9736 473.066 46.1201 472.335 46.1201C471.457 46.1201 470.799 45.8272 470.287 45.2416C469.774 44.6559 469.628 43.7774 469.701 42.6794C469.774 41.5813 470.14 40.6296 470.725 40.0439ZM489.525 36.164C487.331 36.164 485.502 36.8228 484.112 37.9941C482.722 39.2387 481.917 40.776 481.698 42.6794C481.479 44.7291 482.064 46.3397 483.454 47.5842C484.551 48.6091 486.16 49.1215 488.135 49.1215C490.403 49.1215 492.232 48.5359 493.622 47.2914C495.011 46.0468 495.816 44.5095 495.962 42.6061C496.109 40.9224 495.743 39.4583 494.865 38.3602C493.841 36.896 492.012 36.164 489.525 36.164ZM491.573 42.6794C491.427 43.9239 491.134 44.8755 490.549 45.4612C489.964 46.0468 489.306 46.3397 488.574 46.3397C487.77 46.3397 487.184 46.0468 486.745 45.388C486.307 44.8023 486.16 43.8507 486.233 42.6794C486.38 41.508 486.672 40.6296 487.257 40.0439C487.843 39.4583 488.501 39.1654 489.306 39.1654C490.037 39.1654 490.696 39.4583 491.134 40.1171C491.573 40.6296 491.72 41.508 491.573 42.6794ZM516.81 36.164C515.933 36.164 515.128 36.3104 514.47 36.6032C513.811 36.896 513.08 37.4817 512.275 38.287L512.056 38.5066L511.909 38.2138C511.617 37.5549 511.178 37.0425 510.666 36.6764C510.154 36.3104 509.422 36.164 508.398 36.164C507.52 36.164 506.716 36.3104 505.984 36.6032C505.326 36.896 504.594 37.4817 503.863 38.2138L503.351 38.7262L503.57 36.3836H499.474L498.23 48.7555H502.692L503.351 42.3865C503.424 41.3616 503.716 40.6296 504.155 40.1171C504.594 39.6047 505.179 39.3851 505.765 39.3851C506.204 39.3851 506.496 39.5315 506.789 39.7511C507.081 39.9707 507.228 40.2635 507.374 40.7028C507.447 40.9224 507.447 41.3616 507.374 41.9473L506.716 48.7555H511.178L511.836 42.3133C511.909 41.2884 512.202 40.6296 512.641 40.1171C513.08 39.6779 513.665 39.3851 514.25 39.3851C514.835 39.3851 515.274 39.6779 515.64 40.1903C515.859 40.4832 515.933 40.9956 515.859 41.6545L515.128 48.7555H519.59L520.395 40.8492C520.541 39.1654 520.322 37.9209 519.663 37.1889C519.078 36.6032 518.127 36.164 516.81 36.164Z" fill="#050505"/>
              <path d="M537 27.4524C537 31.918 533.489 35.5051 528.88 35.5051C524.345 35.5051 520.688 31.9912 520.688 27.4524C520.688 23.0601 524.272 19.5461 528.88 19.5461C533.416 19.5461 537 23.0601 537 27.4524ZM522.736 27.4524C522.736 30.9663 525.369 33.7482 528.88 33.7482C532.392 33.7482 534.879 30.9663 534.879 27.4524C534.879 23.9385 532.319 21.0835 528.807 21.0835C525.296 21.0835 522.736 23.9385 522.736 27.4524ZM527.637 31.6252H525.808V23.6457C526.54 23.4993 527.564 23.4261 528.88 23.4261C530.343 23.4261 531.075 23.6457 531.66 24.0117C532.099 24.3778 532.465 24.9634 532.465 25.7687C532.465 26.6472 531.806 27.306 530.855 27.5989V27.6721C531.66 27.9649 532.099 28.5505 532.318 29.5754C532.538 30.7467 532.684 31.2592 532.904 31.552H530.929C530.709 31.2592 530.563 30.5271 530.27 29.6486C530.124 28.7702 529.612 28.4041 528.661 28.4041H527.783V31.6252H527.637ZM527.637 27.0864H528.515C529.539 27.0864 530.343 26.7204 530.343 25.9151C530.343 25.183 529.831 24.7438 528.661 24.7438C528.149 24.7438 527.856 24.817 527.637 24.817V27.0864Z" fill="#050505"/>
            </g>
            <defs><clipPath id="clip0_print"><rect width="537" height="50" fill="white"/></clipPath></defs>
          </svg>
          <div class="company-contact">
            ${contactParts.map(part => `<span>${part}</span>`).join('')}
          </div>
        </div>

        <div class="product-header">
          <h1>${data.title}</h1>
          ${data.variantTitle && data.variantTitle !== data.title ? `<h2>${data.variantTitle}</h2>` : ''}
          <p class="meta">Quantity: ${data.quantity} &bull; Unit Price: ${this.formatPrice(data.price, data.currency)}</p>
          ${data.url ? `<p class="meta">Product: ${companyUrl}${data.url}</p>` : ''}
        </div>

        ${bespokeHtml}
        ${requiredAddonsHtml}
        ${optionalAddonsHtml}

        <div class="total">
          <span class="total-label">Bundle Total</span>
          <span class="total-value">${this.formatPrice(totalPrice, data.currency)}</span>
        </div>

        <p class="disclaimer">This configuration summary is for reference only and does not constitute a quotation. Prices shown are indicative and may be subject to change. Please confirm final pricing at checkout.</p>
      </body>
      </html>
    `;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new CartPrintSummary();
});
